import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Bell, AlertTriangle, Info, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useAppStore } from '../../store/useAppStore';
import type { Alert } from '../../types';

function SeverityIcon({ severity }: { severity: Alert['severity'] }) {
  if (severity === 'error') return <AlertTriangle className="w-5 h-5 text-loss" />;
  if (severity === 'warning') return <AlertTriangle className="w-5 h-5 text-warn" />;
  return <Info className="w-5 h-5 text-info" />;
}

function severityBorderClass(severity: Alert['severity']) {
  if (severity === 'error') return 'border-loss/30';
  if (severity === 'warning') return 'border-warn/30';
  return 'border-info/30';
}

export function MobileAlertSheet() {
  const { alertSheetOpen, alertSheetItem, closeAlertSheet, markAlertRead } = useAppStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (alertSheetOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [alertSheetOpen]);

  if (!alertSheetOpen || !alertSheetItem) return null;

  const alert = alertSheetItem;

  function handleViewGame() {
    if (!alert.gamePk) return;
    closeAlertSheet();
    void navigate(`/baseball/mlb/game/${alert.gamePk}`);
  }

  function handleDismiss() {
    markAlertRead(alert.id);
    closeAlertSheet();
  }

  return (
    <>
      {/* Backdrop */}
      <motion.div
        className="fixed inset-0 z-[60] bg-black/60 md:hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={closeAlertSheet}
      />

      {/* Sheet — 50% height */}
      <motion.div
        className={clsx(
          'fixed inset-x-0 bottom-0 z-[60] flex flex-col md:hidden',
          'bg-surface-1 rounded-t-2xl border-t-2',
          severityBorderClass(alert.severity),
        )}
        style={{ height: '50vh' }}
        initial={{ y: '50vh' }}
        animate={{ y: 0 }}
        exit={{ y: '50vh' }}
        transition={{ type: 'spring', stiffness: 400, damping: 40 }}
      >
        {/* Handle */}
        <div className="flex items-center justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-surface-4" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">Alert</span>
          </div>
          <button
            onClick={closeAlertSheet}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-300 hover:bg-surface-3 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <SeverityIcon severity={alert.severity} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-100 leading-snug">{alert.title}</p>
              {alert.body && (
                <p className="text-xs text-gray-400 mt-1.5 leading-relaxed">{alert.body}</p>
              )}
              <p className="text-[10px] text-gray-600 mt-2">
                {new Date(alert.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex gap-2 px-4 py-3 border-t border-surface-4 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium bg-surface-3 text-gray-400 hover:text-gray-200 active:scale-95 transition-all"
          >
            Dismiss
          </button>
          {alert.gamePk && (
            <button
              onClick={handleViewGame}
              className="flex items-center justify-center gap-1.5 flex-1 py-2.5 rounded-lg text-sm font-medium bg-brand text-white hover:bg-brand-dark active:scale-95 transition-all"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View Game
            </button>
          )}
        </div>
      </motion.div>
    </>
  );
}
